import { API, DOMAIN } from '@autopass-web/domain';
import axios from 'axios';
import type {
  CreateApplePaySessionValidtionQuery,
  AuthApsApplePayQuery,
  CreateLinePaySessionQuery,
  CreateWalletSessionQuery,
  CreateCreditCardSessionQuery,
  AuthApsCreditCardQuery,
  ApsGetOrderQuery,
  AuthApsWalletPayQuery,
} from './request-types';
import type {
  CreateLinePaySessionResponse,
  CreateWalletSessionResponse,
  CreateCreditCardSessionResponse,
  ConfirmCreditCardTransactionResponse,
  ApsGetOrderResponse,
  AuthApsWalletPayResponse,
} from './response-types';

const API_CONFIG = DOMAIN.API_URL[API.USER];

const axiosInstance = axios.create({
  headers: API_CONFIG.headers,
  baseURL: API_CONFIG.baseUrl,
  timeout: 60 * 1000,
  validateStatus: (status) => {
    return status >= 200 && status < 300;
  },
});

/**
 * @swagger
 *
 * the document may not be comprehensive, please double-check the response value.
 * https://storage.googleapis.com/pklotcrop-swagger-doc/79455a04b9bb8e812379726aea734db9/dist/index.html?url=../api3.1666666703.json
 */

class Client {
  // User API
  createApplePaySessionValidtion = ({
    transactionType,
    sessionUrl,
  }: CreateApplePaySessionValidtionQuery) =>
    axiosInstance.post<unknown>(`/apple_pay/session_validation`, {
      session_url: sessionUrl,
      transaction_type: transactionType,
    });
  createLinePaySession = ({
    parkingTicketId,
    callbackUrl,
    email,
    receipt,
  }: CreateLinePaySessionQuery) =>
    axiosInstance.post<CreateLinePaySessionResponse>(
      `/parking_tickets/line_pay/init`,
      {
        parking_ticket_id: parkingTicketId,
        email,
        receipt,
        callback_url: callbackUrl,
      },
    );

  createWalletSession = ({
    parkingTicketId,
    email,
    receipt,
    callbackUrl,
    walletType,
  }: CreateWalletSessionQuery) =>
    axiosInstance.post<CreateWalletSessionResponse>(
      `/parking_tickets/${walletType}/init`,
      {
        parking_ticket_id: parkingTicketId,
        email,
        type: 'web',
        callback_url: callbackUrl,
        receipt,
      },
    );

  createCreditCardSession = ({
    parkingTicketId,
    email,
    receipt,
  }: CreateCreditCardSessionQuery) =>
    axiosInstance.post<CreateCreditCardSessionResponse>(
      `/parking_tickets/${parkingTicketId}/create_transaction`,
      {
        // If email is not provided, [the value = null] is necessary
        email: email ?? null,
        receipt,
      },
    );

  authApsApplePay = ({
    parkingTicketId,
    applePayPaymentInfo,
    email,
    receipt,
  }: AuthApsApplePayQuery) =>
    axiosInstance.post(`/parking_tickets/apple_pay/pay`, {
      parking_ticket_id: parkingTicketId,
      apple_pay_payment_info: applePayPaymentInfo,
      billing_contact: {
        email_address: email,
      },
      type: 'web',
      receipt,
    });
  authApsCreditCard = ({
    parkingTicketTransactionId,
    token,
    transactionId,
  }: AuthApsCreditCardQuery) =>
    axiosInstance.post<ConfirmCreditCardTransactionResponse>(
      `/parking_tickets/credit_card/pay`,
      {
        parking_ticket_transaction_id: parkingTicketTransactionId,
        token,
        transaction_id: transactionId,
      },
    );

  authApsWalletPay = ({ walletType, transactionId }: AuthApsWalletPayQuery) =>
    axiosInstance.post<AuthApsWalletPayResponse>(
      `/parking_tickets/${walletType}/confirm`,
      {
        transaction_id: transactionId,
      },
    );

  apsGetOrder = ({ parkingTicketId, parkingOrderId }: ApsGetOrderQuery) =>
    axiosInstance.get<ApsGetOrderResponse>(
      `/parking_tickets/${parkingTicketId}/orders/${parkingOrderId}`,
    );

  validateBarcode = ({ barcode }: { barcode: string }) =>
    axiosInstance.get<{ data: { status: 'valid' } }>(
      `/einvoice/validate_mobile_barcode?barcode=${encodeURIComponent(
        barcode,
      )}`,
    );
}

export default new Client();
